// pages/Landing/LandingPro.jsx
import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import "./landing-pro.css";
import { useNavigate } from "react-router-dom";
import ChatWidget from "../../components/ChatWidget";

// Configuración de precios
const PRICING = {
  ecf: 30,
  plans: {
    basico: 60,
    plus: 120,
    enterprise: "personalizado"
  }
};

// Constante para días de demo
const DEMO_DAYS = 14;

// Storage keys
const STORAGE_KEYS = {
  FORM_DATA: "efit_landing_form_data",
  LEAD_TYPE: "efit_lead_type"
};

// JSON-LD Schema para Google
const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "eFIT",
  "applicationCategory": "BusinessApplication",
  "applicationSubCategory": "ERP",
  "operatingSystem": "Web",
  "areaServed": {
    "@type": "Country",
    "name": "República Dominicana",
    "sameAs": "https://www.wikidata.org/wiki/Q786"
  },
  "offers": [
    {
      "@type": "Offer",
      "name": "Plan Básico",
      "price": "60",
      "priceCurrency": "USD",
      "priceSpecification": { "@type": "UnitPriceSpecification", "billingDuration": "P1M" }
    },
    {
      "@type": "Offer",
      "name": "Plan Plus",
      "price": "120",
      "priceCurrency": "USD",
      "priceSpecification": { "@type": "UnitPriceSpecification", "billingDuration": "P1M" }
    }
  ],
  "description": "Sistema ERP con POS, inventario, contabilidad y facturación electrónica (e-CF) para negocios en República Dominicana. Compatible con DGII.",
  "url": "https://efit.com.do"
};

// ── Planes por vertical ──────────────────────────────────────────
const VERTICALES = [
  {
    id: "comercial",
    icono: "🏪",
    nombre: "Comercial",
    desc: "Comercios y servicios",
    planes: [
      {
        nombre: "Comercial Básico",
        precio: 60,
        tagline: "Para empezar a vender con orden",
        limites: ["3 usuarios", "500 productos", "200 facturas/mes", "1 almacén"],
        modulos: [
          "POS / Punto de venta",
          "Inventario en tiempo real",
          "Facturación con NCF",
          "Gestión de clientes",
          "Cotizaciones rápidas",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "CRM +addon", "Terminal datafono +addon"],
      },
      {
        nombre: "Comercial Plus",
        precio: 120,
        popular: true,
        tagline: "Control total de tu negocio",
        limites: ["10 usuarios", "5,000 productos", "2,000 facturas/mes", "3 almacenes"],
        modulos: [
          "Todo el plan Básico",
          "Contabilidad automatizada",
          "Banco y tesorería",
          "Cuentas por pagar (CxP)",
          "Órdenes de compra",
          "CRM integrado",
          "Nómina ISR/TSS 2026",
          "Reportes DGII (606,607,608)",
          "Activos fijos",
          "Auditoría interna",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "Terminal datafono +addon"],
      },
    ],
  },
  {
    id: "constructor",
    icono: "🏗️",
    nombre: "Constructor",
    desc: "Construcción e ingeniería",
    planes: [
      {
        nombre: "Constructor Pro",
        precio: 120,
        tagline: "Gestión de proyectos y obra",
        limites: ["5 usuarios", "Proyectos ilimitados", "500 facturas/mes", "3 almacenes"],
        modulos: [
          "Proyectos y control financiero",
          "Partidas de presupuesto",
          "Certificaciones de avance",
          "Préstamo y transferencia de materiales",
          "Inventario de materiales",
          "Facturación con NCF",
          "CxC — Cuentas por cobrar",
          "Cotizaciones de obra",
          "Gestión de clientes",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "CRM +addon", "Terminal datafono +addon"],
      },
      {
        nombre: "Constructor Plus",
        precio: 150,
        popular: true,
        tagline: "Operación y finanzas completas",
        limites: ["15 usuarios", "Proyectos ilimitados", "Facturas ilimitadas", "5 almacenes"],
        modulos: [
          "Todo el plan Constructor Pro",
          "Contabilidad automatizada",
          "Banco y tesorería",
          "Cuentas por pagar (CxP)",
          "Órdenes de compra",
          "Nómina ISR/TSS 2026",
          "Reportes DGII completos",
          "Activos fijos",
          "CRM integrado",
          "Auditoría interna",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "Terminal datafono +addon"],
      },
    ],
  },
  {
    id: "contable",
    icono: "📊",
    nombre: "Contable",
    desc: "Oficinas contables",
    planes: [
      {
        nombre: "Contable Starter",
        precio: 60,
        tagline: "Para iniciar tu práctica contable",
        limites: ["3 usuarios", "1 empresa cliente", "Facturas ilimitadas"],
        modulos: [
          "Contabilidad completa",
          "Banco y tesorería",
          "Cuentas por pagar y cobrar",
          "Activos fijos y depreciación",
          "Nómina ISR/TSS 2026",
          "Reportes DGII (606,607,608,IR-2)",
          "Importación de datos",
          "Auditoría interna",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "CRM +addon"],
      },
      {
        nombre: "Contable Pro",
        precio: 90,
        recomendado: true,
        tagline: "Hasta 5 empresas cliente",
        limites: ["5 usuarios", "5 empresas cliente", "Facturas ilimitadas"],
        modulos: [
          "Todo el plan Starter",
          "Multi-empresa (hasta 5 RNC)",
          "Panel por empresa separado",
          "Balances individuales por empresa",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "CRM +addon"],
      },
      {
        nombre: "Contable Plus",
        precio: 130,
        tagline: "Hasta 10 empresas cliente",
        limites: ["8 usuarios", "10 empresas cliente", "Facturas ilimitadas"],
        modulos: [
          "Todo el plan Pro",
          "Multi-empresa (hasta 10 RNC)",
          "Gestión centralizada de clientes",
          "Reportes consolidados",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "CRM +addon"],
      },
    ],
  },
  {
    id: "rentacar",
    icono: "🚗",
    nombre: "Renta-car",
    desc: "Alquiler de vehículos",
    planes: [
      {
        nombre: "Renta-car Básico",
        precio: 89,
        tagline: "Control de flota y rentas",
        limites: ["3 usuarios", "50 vehículos", "200 facturas/mes"],
        modulos: [
          "Gestión de flota vehicular",
          "Rentas y contratos",
          "Inspecciones de vehículos",
          "Facturación con NCF",
          "CxC — Cuentas por cobrar",
          "Cotizaciones de renta",
          "Gestión de clientes",
        ],
        addons: ["e-CF Facturación Electrónica +$30", "CRM +addon"],
      },
      {
        nombre: "Renta-car Plus",
        precio: 120,
        popular: true,
        tagline: "Operación y finanzas completas",
        limites: ["10 usuarios", "Vehículos ilimitados", "Facturas ilimitadas"],
        modulos: [
          "Todo el plan Básico",
          "Contabilidad automatizada",
          "Banco y tesorería",
          "Cuentas por pagar (CxP)",
          "Nómina ISR/TSS 2026",
          "Reportes DGII completos",
          "Activos fijos",
          "CRM integrado",
          "Auditoría interna",
        ],
        addons: ["e-CF Facturación Electrónica +$30"],
      },
    ],
  },
  {
    id: "enterprise",
    icono: "🏢",
    nombre: "Enterprise",
    desc: "Multi-vertical · Personalizado",
    planes: [
      {
        nombre: "Enterprise",
        precio: 250,
        tagline: "Todo incluido · Sin límites · Soporte dedicado",
        limites: ["Usuarios ilimitados", "Empresas ilimitadas", "Facturas ilimitadas", "Almacenes ilimitados"],
        modulos: [
          "Todos los módulos Comercial + Constructor",
          "Todos los módulos Contable + Renta-car",
          "e-CF Facturación Electrónica incluido",
          "Multi-empresa sin límite",
          "API personalizada",
          "Migración de datos incluida",
          "Implementación dedicada",
          "Soporte prioritario 24/7",
        ],
        addons: [],
      },
    ],
  },
];

// ── Planes por industria (selector) ──────────────────────────────
const PLANS_BY_INDUSTRY = {
  comercial: [
    {
      name: "Starter", price: "$60", planKey: "basico", highlight: false,
      tagline: "Para empezar a vender con orden",
      limits: ["3 usuarios", "500 productos", "200 facturas/mes"],
      features: ["POS / Punto de venta", "Inventario en tiempo real", "Facturación con NCF", "Gestión de clientes", "Cotizaciones rápidas"],
    },
    {
      name: "Pro", price: "$120", planKey: "plus", highlight: true,
      tagline: "Cierra tu mes sin contratar un contador",
      limits: ["10 usuarios", "5,000 productos", "2,000 facturas/mes"],
      features: ["Todo el plan Starter", "Contabilidad automatizada", "Banco y tesorería", "CxP / CxC", "Nómina ISR/TSS 2026", "Reportes DGII (606,607,608)", "CRM integrado"],
    },
    {
      name: "Scale", price: "$180", planKey: "plus", highlight: false,
      tagline: "Para negocios con múltiples almacenes",
      limits: ["Usuarios ilimitados", "Productos ilimitados", "Facturas ilimitadas"],
      features: ["Todo el plan Pro", "Multi-almacén sin límite", "Activos fijos", "Auditoría interna", "Soporte prioritario"],
    },
  ],
  constructor: [
    {
      name: "Starter", price: "$99", planKey: "basico", highlight: false,
      tagline: "Gestión de proyectos y obra",
      limits: ["5 usuarios", "Proyectos ilimitados", "500 facturas/mes"],
      features: ["Proyectos y control financiero", "Partidas de presupuesto", "Certificaciones de avance", "Inventario de materiales", "Facturación con NCF", "CxC — Cuentas por cobrar"],
    },
    {
      name: "Pro", price: "$150", planKey: "plus", highlight: true,
      tagline: "Sabe cuánto cuesta cada obra antes de terminarla",
      limits: ["15 usuarios", "Proyectos ilimitados", "Facturas ilimitadas"],
      features: ["Todo el plan Starter", "Contabilidad automatizada", "Banco y tesorería", "Nómina ISR/TSS 2026", "Reportes DGII completos", "CRM integrado"],
    },
    {
      name: "Scale", price: "$199", planKey: "plus", highlight: false,
      tagline: "Multi-obra, multi-empresa, sin límites",
      limits: ["Usuarios ilimitados", "5+ almacenes", "Facturas ilimitadas"],
      features: ["Todo el plan Pro", "Activos fijos", "Auditoría interna", "Soporte prioritario"],
    },
  ],
  contable: [
    {
      name: "Starter", price: "$60", planKey: "basico", highlight: false,
      tagline: "Para iniciar tu práctica contable",
      limits: ["3 usuarios", "1 empresa cliente", "Facturas ilimitadas"],
      features: ["Contabilidad completa", "Banco y tesorería", "CxP y CxC", "Activos fijos y depreciación", "Nómina ISR/TSS 2026", "Reportes DGII (606,607,608,IR-2)"],
    },
    {
      name: "Pro", price: "$99", planKey: "plus", highlight: true,
      tagline: "Maneja 5 empresas sin perder el control de ninguna",
      limits: ["5 usuarios", "5 empresas cliente", "Facturas ilimitadas"],
      features: ["Todo el plan Starter", "Multi-empresa (hasta 5 RNC)", "Panel por empresa separado", "Balances individuales por empresa"],
    },
    {
      name: "Scale", price: "$149", planKey: "plus", highlight: false,
      tagline: "Hasta 10 empresas, gestión centralizada",
      limits: ["8 usuarios", "10 empresas cliente", "Facturas ilimitadas"],
      features: ["Todo el plan Pro", "Multi-empresa (hasta 10 RNC)", "Gestión centralizada de clientes", "Reportes consolidados"],
    },
  ],
  renta_car: [
    {
      name: "Starter", price: "$89", planKey: "basico", highlight: false,
      tagline: "Control de flota y rentas",
      limits: ["3 usuarios", "50 vehículos", "200 facturas/mes"],
      features: ["Gestión de flota vehicular", "Rentas y contratos", "Inspecciones de vehículos", "Facturación con NCF", "CxC — Cuentas por cobrar", "Cotizaciones de renta"],
    },
    {
      name: "Pro", price: "$130", planKey: "plus", highlight: true,
      tagline: "Nunca más un vehículo rentado sin contrato firmado",
      limits: ["10 usuarios", "Vehículos ilimitados", "Facturas ilimitadas"],
      features: ["Todo el plan Starter", "Contabilidad automatizada", "Banco y tesorería", "Nómina ISR/TSS 2026", "Reportes DGII completos", "CRM integrado"],
    },
    {
      name: "Scale", price: "$179", planKey: "plus", highlight: false,
      tagline: "Multi-flota, sin límites operativos",
      limits: ["Usuarios ilimitados", "Vehículos ilimitados", "Facturas ilimitadas"],
      features: ["Todo el plan Pro", "Activos fijos", "Auditoría interna", "Soporte prioritario"],
    },
  ],
};

const INDUSTRY_LIST = [
  { id: "comercial",   label: "🏪 Comercio",       sub: "Retail y servicios" },
  { id: "constructor", label: "🏗️ Construcción",    sub: "Ingeniería y obra" },
  { id: "renta_car",   label: "🚗 Renta-car",       sub: "Alquiler de vehículos" },
  { id: "contable",    label: "📊 Firma contable",  sub: "Oficinas contables" },
  { id: "enterprise",  label: "🏢 Empresa grande",  sub: "Multi-vertical" },
];

// ── Módulos del sistema ───────────────────────────────────────────
const MODULOS = [
  {
    id: "pos",
    icono: "💳",
    nombre: "POS / Caja",
    resumen: "Punto de venta rápido y completo",
    descripcion: "Sistema de punto de venta diseñado para negocios en República Dominicana. Procesa ventas en segundos, maneja múltiples métodos de pago, controla cajas físicas por turno y genera reportes en tiempo real — todo integrado con inventario y contabilidad automáticamente.",
    caracteristicas: [
      {
        icono: "💵",
        titulo: "Múltiples métodos de pago",
        desc: "Acepta efectivo, tarjeta (crédito/débito), transferencia bancaria y crédito a cliente en una misma venta. También soporta pago mixto: el cliente puede pagar parte en efectivo y parte con tarjeta en una sola transacción."
      },
      {
        icono: "🖨️",
        titulo: "Impresión de recibo",
        desc: "Genera recibo automático con subtotal, ITBIS desglosado y total. Compatible con impresoras térmicas de 58mm y 80mm. El recibo incluye el nombre del cajero, fecha, hora y número de transacción."
      },
      {
        icono: "🗄️",
        titulo: "Apertura y cierre de caja",
        desc: "Flujo completo de turno: el cajero abre la caja con el monto inicial en efectivo y al cerrar el sistema muestra el cuadre — lo esperado vs. lo contado. Si hay faltante, se requiere justificación antes de cerrar."
      },
      {
        icono: "❌",
        titulo: "Anulación de ventas",
        desc: "Anula una venta ya procesada en segundos. El inventario se restaura automáticamente y se genera el asiento contable de reversión (débito ingreso / crédito caja). Queda registrado quién anuló y por qué."
      },
      {
        icono: "📊",
        titulo: "Reportes de ventas",
        desc: "Dashboard con ventas del día, semana y mes. Desglose por cajero, por método de pago y por producto más vendido. Exportable para revisión gerencial sin necesidad de contabilidad adicional."
      },
      {
        icono: "📦",
        titulo: "Inventario en tiempo real",
        desc: "Cada venta descuenta automáticamente el stock. Si un producto llega a cero, el sistema alerta al cajero antes de intentar venderlo. No se requiere ajuste manual al final del día."
      },
      {
        icono: "🔍",
        titulo: "Búsqueda por código de barras",
        desc: "Soporta lectores USB HID (pistola de código de barras). Al escanear el producto aparece instantáneamente con su precio, ITBIS y stock disponible. También se puede buscar por nombre o código interno."
      },
      {
        icono: "🏧",
        titulo: "Terminal datafono integrado",
        desc: "Integración nativa con terminales Verifone, PAX e Ingenico vía protocolo TCP/ECR y HTTP. El agente local corre en Windows o Linux — el monto se envía al datafono automáticamente sin teclear en el dispositivo."
      },
      {
        icono: "💱",
        titulo: "ITBIS automático",
        desc: "El ITBIS (18%) se calcula automáticamente por producto según su configuración fiscal. Los productos exentos no generan ITBIS. El recibo siempre muestra el desglose correcto para cumplimiento DGII."
      },
      {
        icono: "👤",
        titulo: "Control por cajero",
        desc: "Cada cajero tiene su propia sesión. El sistema registra qué vendió cada uno, en qué turno y con qué métodos de pago. El supervisor puede ver el histórico de cualquier cajero sin mezclar datos."
      }
    ]
  },
  {
    id: "inventario",
    icono: "📦",
    nombre: "Inventario",
    resumen: "Control de stock en tiempo real",
    descripcion: "Gestión completa de almacenes, entradas, salidas, transferencias entre sucursales e inventario físico. Cada movimiento genera su asiento contable automáticamente.",
    caracteristicas: [
      { icono: "📥", titulo: "Entradas de mercancía", desc: "Registra recepciones con o sin orden de compra. Actualiza el stock automáticamente y genera el asiento contable de inventario." },
      { icono: "📤", titulo: "Salidas y consumos", desc: "Controla salidas por venta, consumo interno o baja. Cada salida queda trazada con fecha, responsable y motivo." },
      { icono: "🔄", titulo: "Transferencias entre almacenes", desc: "Mueve mercancía entre sucursales o almacenes. El sistema registra el origen, destino y cantidad transferida." },
      { icono: "📋", titulo: "Inventario físico", desc: "Módulo de conteo físico con comparativo entre stock del sistema vs. contado. Genera el ajuste automáticamente." },
      { icono: "🚨", titulo: "Alertas de stock mínimo", desc: "Configura el mínimo por producto. Cuando el stock baja de ese nivel, el sistema alerta para reabastecimiento." },
      { icono: "🏷️", titulo: "Categorías y SKU", desc: "Organiza productos por categoría, subcategoría y código SKU. Búsqueda rápida por nombre o código de barras." }
    ]
  },
  {
    id: "facturacion",
    icono: "📄",
    nombre: "Facturación e-CF",
    resumen: "Facturación electrónica DGII integrada",
    descripcion: "Emite facturas con NCF válidos, envía comprobantes electrónicos (e-CF) a la DGII automáticamente y gestiona notas de crédito y débito. 100% compatible con la normativa dominicana.",
    caracteristicas: [
      { icono: "🔢", titulo: "NCF automáticos", desc: "Genera comprobantes B01, B02, B14, B15, E31, E32 y más. Las secuencias se administran automáticamente sin riesgo de duplicados." },
      { icono: "📡", titulo: "Envío e-CF a DGII", desc: "Transmite el comprobante electrónico a la DGII vía Alanube en segundos. El sistema reintenta automáticamente si hay fallo de conexión." },
      { icono: "📝", titulo: "Notas de crédito y débito", desc: "Emite E33/E34 referenciando la factura original. El sistema aplica el ajuste al balance del cliente automáticamente." },
      { icono: "💰", titulo: "Control de CxC", desc: "Dashboard de cuentas por cobrar con aging por cliente, facturas vencidas y pendientes de pago." },
      { icono: "🖨️", titulo: "PDF representación impresa", desc: "Genera el PDF oficial de representación impresa con código QR de verificación DGII." },
      { icono: "🔄", titulo: "Reintentos automáticos", desc: "Si la DGII no responde, el sistema reintenta cada hora hasta 3 veces. Si falla, notifica al administrador por email." }
    ]
  },
  {
    id: "contabilidad",
    icono: "📊",
    nombre: "Contabilidad",
    resumen: "Asientos automáticos y cierre mensual",
    descripcion: "Contabilidad integrada con todos los módulos. Cada venta, pago, factura y nómina genera su asiento contable automáticamente. Cierre mensual en minutos, no en días.",
    caracteristicas: [
      { icono: "⚡", titulo: "Asientos automáticos", desc: "Factura emitida, pago recibido, nota de crédito, pago de nómina — cada evento genera su asiento sin intervención del contador." },
      { icono: "📖", titulo: "Plan de cuentas", desc: "Plan de cuentas estándar dominicano pre-cargado. Personalizable por empresa. Soporta múltiples empresas contables bajo el mismo tenant." },
      { icono: "🔒", titulo: "Cierre contable", desc: "Cierra el período mensual con un click. Una vez cerrado, no se pueden modificar los asientos de ese período." },
      { icono: "⚖️", titulo: "Balance general", desc: "Balance general y estado de resultados en tiempo real. Siempre cuadrado: Activos = Pasivos + Capital." },
      { icono: "🏢", titulo: "Multi-empresa", desc: "Lleva la contabilidad de múltiples empresas bajo el mismo usuario. Cada empresa tiene su propio plan de cuentas y balance." },
      { icono: "🔐", titulo: "PIN por empresa", desc: "Acceso protegido por PIN de 4 dígitos por empresa contable. Con fallback a contraseña del administrador." }
    ]
  },
  {
    id: "dgii",
    icono: "📋",
    nombre: "Reportes DGII",
    resumen: "606, 607, 608 y más — automáticos",
    descripcion: "Genera todos los reportes fiscales requeridos por la DGII de República Dominicana automáticamente desde los datos del sistema. Exporta el TXT listo para cargar al portal de la DGII.",
    caracteristicas: [
      { icono: "📄", titulo: "Formato 606 — Compras", desc: "Reporte de compras a proveedores con RNC, tipo de gasto e ITBIS. Exportación TXT para DGII." },
      { icono: "📄", titulo: "Formato 607 — Ventas", desc: "Reporte de ventas con NCF, RNC del comprador, monto e ITBIS. Se genera desde las facturas emitidas." },
      { icono: "📄", titulo: "Formato 608 — Anulaciones", desc: "Reporta los comprobantes anulados en el período. Se alimenta automáticamente de las facturas anuladas." },
      { icono: "📄", titulo: "IT-1 e IR-2", desc: "Declaración de ITBIS (IT-1) e Impuesto sobre la Renta de sociedades (IR-2) con todos los campos requeridos." },
      { icono: "📄", titulo: "Reporte 609 — Exterior", desc: "Pagos a proveedores extranjeros con asiento contable automático de diferencia cambiaria." },
      { icono: "👤", titulo: "IR-3 — Negocio de Único Dueño (Persona Física)", desc: "Aplica escala progresiva (0%–25%) en lugar del 27% flat de las empresas. Fecha límite de presentación: 31 de marzo del año siguiente (o 120 días desde el cierre fiscal)." }
    ]
  },
  {
    id: "crm",
    icono: "👥",
    nombre: "CRM",
    resumen: "Pipeline de ventas y seguimiento",
    descripcion: "Gestiona todo el ciclo de ventas: desde el primer contacto hasta el cierre. Pipeline visual, seguimientos programados y conversión automática a cliente y proyecto al cerrar.",
    caracteristicas: [
      { icono: "🎯", titulo: "Pipeline visual", desc: "Etapas: Contacto → Necesidad validada → Propuesta → Negociación → Cerrado. Arrastra las oportunidades entre etapas." },
      { icono: "📞", titulo: "Seguimientos", desc: "Programa llamadas, visitas, emails y reuniones. El sistema recuerda los pendientes del día." },
      { icono: "💼", titulo: "Prospectos desde landing", desc: "Los leads del formulario web llegan automáticamente al CRM como prospectos nuevos listos para contactar." },
      { icono: "✅", titulo: "Cierre automático", desc: "Al cerrar una oportunidad como ganada, el sistema crea el cliente y el proyecto automáticamente." },
      { icono: "📊", titulo: "Dashboard de pipeline", desc: "Valor total por etapa, oportunidades vencidas y tasa de conversión del equipo comercial." },
      { icono: "🔀", titulo: "Reasignación", desc: "Los supervisores pueden reasignar prospectos y oportunidades entre vendedores con un click." }
    ]
  },
  {
    id: "nomina",
    icono: "👤",
    nombre: "Nómina",
    resumen: "ISR y TSS 2026 calculados automáticamente",
    descripcion: "Nómina quincenal y mensual con cálculo automático de ISR, SFS y AFP según las tasas de la DGII y CNSS 2026. Genera el reporte TSS en formato SUIR+ para la Tesorería.",
    caracteristicas: [
      { icono: "🧮", titulo: "ISR automático 2026", desc: "Escala progresiva DGII 2026 aplicada automáticamente. Deduce cotizaciones antes del cálculo del ISR." },
      { icono: "🏥", titulo: "TSS — SFS y AFP", desc: "Tasas 2026 del CNSS aplicadas sobre el salario cotizable con tope de 20 × RD$23,223." },
      { icono: "📅", titulo: "Nómina quincenal híbrida", desc: "1ª quincena descuenta SFS, AFP e ISR. 2ª quincena es neta sin descuentos. El sistema detecta la quincena automáticamente." },
      { icono: "📋", titulo: "Reporte TSS / CNSS", desc: "Genera el archivo TXT SUIR+ y Excel para la Autodeterminación Mensual del CNSS." },
      { icono: "📊", titulo: "IR-17 consolidado", desc: "Reporte IR-17 por empleado consolidado (una línea por empleado, no por quincena)." },
      { icono: "⚡", titulo: "Asiento automático", desc: "Al procesar la nómina se genera el asiento contable: salarios, TSS patronal, retenciones." }
    ]
  },
  {
    id: "banco",
    icono: "🏦",
    nombre: "Banco / Tesorería",
    resumen: "Conciliación bancaria y flujo de caja",
    descripcion: "Gestiona cuentas bancarias, registra movimientos y concilia el saldo del banco con el libro contable. Cada movimiento genera su asiento automáticamente.",
    caracteristicas: [
      { icono: "🏛️", titulo: "Múltiples cuentas", desc: "Registra todas las cuentas bancarias de la empresa con su saldo actual en tiempo real." },
      { icono: "💸", titulo: "Movimientos bancarios", desc: "Registra depósitos, retiros, transferencias y notas de crédito/débito del banco." },
      { icono: "✅", titulo: "Conciliación bancaria", desc: "Compara el extracto del banco con los movimientos del sistema. Marca los registros conciliados." },
      { icono: "⚡", titulo: "Asiento automático", desc: "Cada movimiento bancario genera su asiento contable automáticamente en la cuenta correcta." },
      { icono: "💰", titulo: "Flujo de caja", desc: "Dashboard de ingresos vs. egresos bancarios por período para control de liquidez." },
      { icono: "🔗", titulo: "Integrado con CxP", desc: "Al pagar una cuenta por pagar desde tesorería, el asiento y el status de la CxP se actualizan solos." }
    ]
  },
  {
    id: "compras",
    icono: "🛒",
    nombre: "Compras / CxP",
    resumen: "Órdenes de compra y cuentas por pagar",
    descripcion: "Gestiona el ciclo completo de compras: orden de compra, recepción de mercancía, cuenta por pagar al proveedor y pago. Todo con asientos contables automáticos.",
    caracteristicas: [
      { icono: "📋", titulo: "Órdenes de compra", desc: "Crea OC con múltiples tipos de ítem: mercancía, servicio, gasto o activo fijo. Cada tipo genera el asiento a la cuenta contable correcta." },
      { icono: "📥", titulo: "Recepción de mercancía", desc: "Al recibir la OC, el inventario se actualiza automáticamente y se crea la CxP al proveedor." },
      { icono: "💳", titulo: "Cuentas por pagar", desc: "Dashboard de CxP con aging por proveedor: corriente, 30, 60 y 90+ días vencido." },
      { icono: "💸", titulo: "Pagos a proveedores", desc: "Registra el pago, genera el asiento contable y marca la CxP como pagada en un solo paso." },
      { icono: "📊", titulo: "Aging de proveedores", desc: "Vista ejecutiva de cuánto se le debe a cada proveedor y cuándo vence cada obligación." },
      { icono: "⚡", titulo: "Asientos automáticos", desc: "Compra de inventario, gasto, activo fijo — cada tipo de ítem usa la cuenta contable correcta automáticamente." }
    ]
  },
  {
    id: "proyectos",
    icono: "🏗️",
    nombre: "Proyectos",
    resumen: "Presupuesto, partidas y certificaciones",
    descripcion: "Control financiero completo de proyectos de construcción, ingeniería o servicios. Presupuesto por partidas, gastos reales, jornales y certificaciones de avance.",
    caracteristicas: [
      { icono: "📐", titulo: "Presupuesto por partidas", desc: "Define el presupuesto del proyecto en partidas estándar con cantidad, unidad y precio unitario." },
      { icono: "💸", titulo: "Gastos reales", desc: "Registra los gastos reales del proyecto. El sistema muestra la desviación vs. presupuesto en tiempo real." },
      { icono: "👷", titulo: "Jornales de mano de obra", desc: "Registra los jornales diarios por trabajador y los integra al costo del proyecto automáticamente." },
      { icono: "📋", titulo: "Certificaciones de avance", desc: "Emite certificaciones de obra con el % de avance por partida para cobro al cliente." },
      { icono: "📊", titulo: "Dashboard financiero", desc: "Vista ejecutiva: presupuesto vs. gastado, % de avance, flujo de caja del proyecto y margen estimado." },
      { icono: "🔗", titulo: "Integrado con CRM", desc: "Al cerrar una oportunidad como ganada en el CRM, el proyecto se crea automáticamente con el presupuesto de la propuesta." }
    ]
  }
];

export default function LandingPro() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nombre: "",
    empresa: "",
    email: "",
    telefono: "",
    rnc_cedula: "",
    plan: "basico",
    requiere_ecf: false,
    tipo_negocio: "comercio",
    website: "",  // honeypot — debe permanecer vacío; los bots lo rellenan
  });

  // Marca de tiempo cuando el formulario se vuelve visible (anti-bot: envíos < 2s)
  const formStartTime = useRef(Date.now());

  const [moduloActivo, setModuloActivo] = useState(null);
  const moduloDetalleRef = useRef(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const toggleModulo = (id) => {
    const nuevo = moduloActivo === id ? null : id;
    setModuloActivo(nuevo);
    if (nuevo) {
      setTimeout(() => {
        moduloDetalleRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
    }
  };

  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [redirectTimer, setRedirectTimer] = useState(null);
  const [leadType, setLeadType] = useState(null);

  // Plan pre-seleccionado desde las cards de pricing
  const [planCtaSeleccionado, setPlanCtaSeleccionado] = useState(null);
  // { nombre: "Constructor Plus", precio: "$150", planKey: "plus", tipoNegocio: "constructor" }

  const [isRestored, setIsRestored] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const savedFormData = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
    if (savedFormData) {
      try {
        const parsed = JSON.parse(savedFormData);
        setFormData(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error("Error restoring form data:", e);
      }
    }
    setIsRestored(true);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isRestored && !showSuccess) {
      localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formData));
    }
  }, [formData, showSuccess, isRestored]);

  useEffect(() => {
    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [redirectTimer]);

  const trackEvent = (eventName, eventData = {}) => {
    const trackingData = {
      event: eventName,
      timestamp: new Date().toISOString(),
      ...eventData,
      page: "landing",
      url: window.location.href,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(trackingData);
    if (window.gtag) window.gtag("event", eventName, eventData);
    if (window.__CHATBOT_DEBUG__) {
      console.group(`%c[eFit Debug] ${eventName}`, "color:#10b981;font-weight:bold");
      console.table(trackingData);
      console.groupEnd();
    }
  };

  // Lee el contexto del chatbot guardado al hacer click en el CTA
  const getChatContext = () => {
    try {
      return JSON.parse(localStorage.getItem("efit_chat_lead_context") || "null");
    } catch (_) { return null; }
  };

  const selectPlan = (plan) => {
    setFormData(prev => ({ ...prev, plan }));
    trackEvent("plan_selected", { plan, source: "plan_card" });
    document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const validations = {
    nombre: (value) => {
      const clean = value?.trim();
      if (!clean || clean.length < 3)
        return { valid: false, message: "El nombre debe tener al menos 3 caracteres" };
      if (clean.length > 100)
        return { valid: false, message: "El nombre es demasiado largo" };
      if (!/^[a-zA-ZáéíóúñÑ\s]+$/.test(clean))
        return { valid: false, message: "El nombre solo debe contener letras y espacios" };
      return { valid: true };
    },
    telefono: (value) => {
      let cleanPhone = value?.replace(/[\s\-\(\)]/g, '') || '';
      const phoneRegex = /^\+?[0-9]{8,15}$/;
      if (!phoneRegex.test(cleanPhone))
        return { valid: false, message: "El teléfono debe tener entre 8 y 15 dígitos. Ejemplo: +18091234567" };
      const digitsOnly = cleanPhone.replace(/^\+/, '');
      if (digitsOnly.length < 8 || digitsOnly.length > 15)
        return { valid: false, message: "El teléfono debe tener entre 8 y 15 dígitos" };
      return { valid: true, cleaned: cleanPhone };
    },
    email: (value) => {
      const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
      if (!value || !emailRegex.test(value))
        return { valid: false, message: "Ingresa un email válido (ej: nombre@empresa.com)" };
      if (value.length > 255)
        return { valid: false, message: "El email es demasiado largo" };
      return { valid: true };
    },
    empresa: (value) => {
      if (!value?.trim())
        return { valid: false, message: "El nombre de la empresa es requerido" };
      if (value.trim().length < 2)
        return { valid: false, message: "El nombre de la empresa es demasiado corto" };
      return { valid: true };
    }
  };

  const validateForm = () => {
    const fields = ['nombre', 'empresa', 'email', 'telefono'];
    for (const field of fields) {
      const validation = validations[field](formData[field]);
      if (!validation.valid) {
        setSubmitError(validation.message);
        document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth" });
        return false;
      }
    }
    return true;
  };

  const cleanPhone = (phone) => phone?.replace(/[\s\-\(\)]/g, '') || '';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    if (submitError) setSubmitError(null);
  };

  const submitEnterpriseLead = async () => {
    const cleanPhoneNumber = cleanPhone(formData.telefono);
    const chatCtx = getChatContext();
    const leadData = {
      nombre: formData.nombre.trim(),
      empresa: formData.empresa.trim(),
      email: formData.email.trim(),
      telefono: cleanPhoneNumber,
      plan: "enterprise",
      tipo_negocio: formData.tipo_negocio,
      requiere_ecf: formData.requiere_ecf,
      lead_type: "enterprise",
      source: chatCtx?.source || "landing",
      ...(chatCtx && {
        chat_session_id: chatCtx.session_id,
        chat_intent:     chatCtx.intent,
        chat_objection:  chatCtx.objection,
        chat_variant:    chatCtx.variant,
      }),
    };
    try {
      const response = await fetch("/api/public/lead-enterprise", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(leadData),
      });
      if (!response.ok && response.status === 404) {
        return fetch("/api/public/registro-demo", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...leadData, plan: "enterprise" }),
        });
      }
      return response;
    } catch (error) {
      console.error("Error submitting enterprise lead:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validateForm()) return;

    if (formData.plan === "enterprise") {
      setLoading(true);
      localStorage.setItem(STORAGE_KEYS.LEAD_TYPE, "enterprise");
      setLeadType("enterprise");
      trackEvent("enterprise_lead_submitted", {
        plan: formData.plan,
        tipo_negocio: formData.tipo_negocio,
        requiere_ecf: formData.requiere_ecf,
        empresa: formData.empresa
      });
      try {
        const response = await submitEnterpriseLead();
        if (response.ok) {
          setSubmittedEmail(formData.email);
          setShowSuccess(true);
          localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
          localStorage.removeItem("efit_chat_lead_context");
          document.getElementById("success-message")?.scrollIntoView({ behavior: "smooth" });
          setFormData({ nombre: "", empresa: "", email: "", telefono: "", rnc_cedula: "", plan: "basico", requiere_ecf: false, tipo_negocio: "comercio" });
        } else {
          let errorMessage = "No pudimos procesar tu solicitud. Por favor intenta nuevamente.";
          try {
            const data = await response.json();
            errorMessage = data?.error || data?.message || errorMessage;
          } catch (e) {}
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error("Enterprise lead error:", error);
        setSubmitError(error.message || "Error al procesar tu solicitud.");
        trackEvent("enterprise_lead_error", { error: error.message });
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setSubmitError(null);
    const emailToSubmit = formData.email;
    const cleanPhoneNumber = cleanPhone(formData.telefono);
    const chatCtx = getChatContext();
    const formSource = chatCtx?.source || "landing";

    trackEvent("form_submit", {
      plan: formData.plan,
      tipo_negocio: formData.tipo_negocio,
      requiere_ecf: formData.requiere_ecf,
      source: formSource,
      ...(chatCtx && { chat_intent: chatCtx.intent, chat_objection: chatCtx.objection, chat_variant: chatCtx.variant }),
    });

    try {
      const response = await fetch("/api/public/registro-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          telefono:        cleanPhoneNumber,
          source:          formSource,
          form_start_time: formStartTime.current,
          ...(chatCtx && {
            chat_session_id: chatCtx.session_id,
            chat_intent:     chatCtx.intent,
            chat_objection:  chatCtx.objection,
            chat_variant:    chatCtx.variant,
          }),
        }),
      });
      let data;
      try { data = await response.json(); } catch (parseError) { throw new Error("Respuesta inválida del servidor"); }

      if (response.ok) {
        trackEvent("demo_success", {
          plan: formData.plan,
          tipo_negocio: formData.tipo_negocio,
          requiere_ecf: formData.requiere_ecf,
          source: formSource,
          ...(chatCtx && { chat_intent: chatCtx.intent, chat_objection: chatCtx.objection }),
        });
        setSubmittedEmail(emailToSubmit);
        setShowSuccess(true);
        localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
        localStorage.removeItem("efit_chat_lead_context");
        setFormData({ nombre: "", empresa: "", email: "", telefono: "", rnc_cedula: "", plan: "basico", requiere_ecf: false, tipo_negocio: "comercio" });
        document.getElementById("success-message")?.scrollIntoView({ behavior: "smooth" });
      } else {
        const errorMessage = data?.error || data?.message;
        trackEvent("demo_error", { plan: formData.plan, error: errorMessage, status: response.status });
        if (response.status === 409)
          throw new Error("Ya tenemos una solicitud con este email. Pronto nos comunicaremos contigo.");
        else if (response.status === 422)
          throw new Error("Datos inválidos. Por favor verifica la información ingresada.");
        else if (response.status >= 500)
          throw new Error("Error en el servidor. Por favor intenta nuevamente en unos minutos.");
        else
          throw new Error(errorMessage || "No pudimos procesar tu solicitud. Intenta nuevamente o contáctanos.");
      }
    } catch (error) {
      let userMessage = error.message;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userMessage = "Error de conexión. Verifica tu internet e intenta nuevamente.";
        trackEvent("demo_network_error", { error: error.message });
      } else {
        trackEvent("demo_backend_error", { error: error.message });
      }
      setSubmitError(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowSuccess(false);
    setSubmittedEmail("");
    setLeadType(null);
    setPlanCtaSeleccionado(null);
    localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
    localStorage.removeItem(STORAGE_KEYS.LEAD_TYPE);
  };

  const scrollToForm = () => {
    document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth" });
  };

  // Mapeo industria (selectedIndustry) → tipo_negocio del form
  // Clave = id en INDUSTRY_LIST, valor = opción del select tipo_negocio
  const INDUSTRY_TO_TIPO = {
    comercial:   "comercio",
    constructor: "constructor",
    contable:    "contable",
    renta_car:   "rentacar",   // INDUSTRY_LIST usa renta_car (con guion bajo)
    enterprise:  "comercio",
  };

  // Etiqueta legible por industria para mostrar en la pill
  const INDUSTRY_LABEL = {
    comercial:   "Comercial",
    constructor: "Constructor",
    contable:    "Contable",
    renta_car:   "Renta-car",
    enterprise:  "Enterprise",
  };

  // Llamado desde los botones CTA de cada card de plan
  const seleccionarPlan = (planKey, industria, nombrePlan, precioPlan) => {
    const tipoNegocio   = INDUSTRY_TO_TIPO[industria] || "comercio";
    const nombreCompleto = industria === "enterprise"
      ? "Enterprise"
      : `${INDUSTRY_LABEL[industria] || industria} · ${nombrePlan}`;
    setPlanCtaSeleccionado({ nombre: nombreCompleto, precio: precioPlan, planKey, tipoNegocio });
    setFormData(prev => ({ ...prev, plan: planKey, tipo_negocio: tipoNegocio }));
    document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const getButtonText = () => {
    if (!loading)
      return formData.plan === "enterprise" ? "📞 Solicitar consultoría" : "🚀 Solicitar información";
    return formData.plan === "enterprise" ? "Enviando solicitud..." : "Enviando solicitud...";
  };

  const plans = {
    basico: {
      name: "Básico",
      price: `$${PRICING.plans.basico}`,
      benefits: ["📊 Controla tus ventas", "📦 Organiza tu inventario", "💰 Ahorra tiempo"]
    },
    plus: {
      name: "Plus",
      price: `$${PRICING.plans.plus}`,
      benefits: ["✅ Cumple con DGII sin errores", "📈 Toma decisiones con datos reales", "👥 Gestiona tu equipo"]
    },
    enterprise: {
      name: "Enterprise",
      price: "Personalizado",
      benefits: ["🏢 Escala sin límites", "🔌 Integra con tus sistemas", "🚀 Soporte premium"]
    }
  };

  return (
    <>
      {/* ── SEO HEAD ───────────────────────────────────────────────── */}
      <Helmet>
        <title>ERP con Facturación Electrónica en República Dominicana | eFIT</title>
        <meta name="description" content="Sistema ERP con POS, inventario, contabilidad y facturación electrónica DGII (e-CF) en República Dominicana. Prueba gratis 14 días. Sin tarjeta de crédito." />
        <meta name="keywords" content="facturación electrónica República Dominicana, sistema POS RD, software contable RD, ERP República Dominicana, DGII e-CF, software administrativo dominicano" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="eFIT" />
        <link rel="canonical" href="https://efit.com.do" />

        {/* Open Graph */}
        <meta property="og:title" content="ERP con Facturación Electrónica en República Dominicana | eFIT" />
        <meta property="og:description" content="POS, inventario, contabilidad y e-CF DGII en un solo sistema. Prueba gratis 14 días." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://efit.com.do" />
        <meta property="og:locale" content="es_DO" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="ERP con Facturación Electrónica | eFIT" />
        <meta name="twitter:description" content="POS, inventario, contabilidad y e-CF en un solo sistema para negocios en RD." />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>

      <div className="landing-container">

        {/* ── HERO ───────────────────────────────────────────────────── */}
        <section className="hero" aria-label="Presentación de eFIT ERP">
          <div className="hero-content">
            <div className="hero-badge">⚡ El ERP que crece con tu negocio</div>

            {/* H1 ÚNICO — crítico para SEO */}
            <h1>
              ERP con Facturación Electrónica en{" "}
              <span className="hero-highlight">República Dominicana</span> (DGII)
            </h1>

            <p className="hero-subtitle">
              POS, inventario, contabilidad y facturación electrónica e-CF en un solo sistema.
              Sin caos, sin multas, sin estrés.
            </p>
            <div className="hero-buttons">
              <button className="btn-primary btn-large" onClick={scrollToForm}>
                🚀 Probar gratis {DEMO_DAYS} días
              </button>
              <button className="btn-secondary btn-large" onClick={() => navigate('/login')}>
                🔐 Iniciar Sesión
              </button>
            </div>
            <div className="hero-urgency">⏳ Solicita información hoy — respuesta en 24 horas</div>
            <div className="hero-trust">
              <span>✓ Sin tarjeta de crédito</span>
              <span>✓ Cancela cuando quieras</span>
              <span>✓ Soporte incluido</span>
            </div>
            <div className="hero-social-proof">
              <div className="avatars">
                <div className="avatar" aria-hidden="true">JP</div>
                <div className="avatar" aria-hidden="true">MC</div>
                <div className="avatar" aria-hidden="true">RV</div>
                <div className="avatar" aria-hidden="true">AL</div>
              </div>
              <p>+500 negocios en RD ya confían en eFIT</p>
            </div>
            <div className="hero-privacy">
              🔒 Tus datos están protegidos y no compartimos tu información
            </div>
          </div>
          <div className="hero-visual" aria-hidden="true">
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">99.9%</span>
                <span className="stat-label">disponibilidad garantizada</span>
              </div>
              <div className="stat">
                <span className="stat-number">3x</span>
                <span className="stat-label">más eficiencia operativa</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── MÓDULOS DEL SISTEMA ────────────────────────────────────── */}
        <section className="modulos-section" aria-labelledby="modulos-heading">
          <div className="section-header">
            <h2 id="modulos-heading">Todo lo que incluye eFIT</h2>
            <p>Selecciona un módulo para ver qué hace</p>
          </div>

          {/* Grid de módulos */}
          <div className="modulos-grid">
            {MODULOS.map((mod) => (
              <button
                key={mod.id}
                className={`modulo-card ${moduloActivo === mod.id ? "modulo-card--activo" : ""}`}
                onClick={() => toggleModulo(mod.id)}
                aria-expanded={moduloActivo === mod.id}
                aria-controls={`detalle-${mod.id}`}
              >
                <span className="modulo-icono" aria-hidden="true">{mod.icono}</span>
                <span className="modulo-nombre">{mod.nombre}</span>
                <span className="modulo-resumen">{mod.resumen}</span>
                <span className="modulo-chevron" aria-hidden="true">
                  {moduloActivo === mod.id ? "▲" : "▼"}
                </span>
              </button>
            ))}
          </div>

          {/* Panel de detalle */}
          {moduloActivo && (() => {
            const mod = MODULOS.find(m => m.id === moduloActivo);
            if (!mod) return null;
            return (
              <div
                id={`detalle-${mod.id}`}
                className="modulo-detalle"
                ref={moduloDetalleRef}
                role="region"
                aria-label={`Detalle del módulo ${mod.nombre}`}
              >
                <div className="modulo-detalle-header">
                  <span className="modulo-detalle-icono" aria-hidden="true">{mod.icono}</span>
                  <div>
                    <h3 className="modulo-detalle-titulo">{mod.nombre}</h3>
                    <p className="modulo-detalle-desc">{mod.descripcion}</p>
                  </div>
                </div>
                <div className="modulo-caracteristicas-grid">
                  {mod.caracteristicas.map((car, i) => (
                    <div className="modulo-caracteristica" key={i}>
                      <span className="caract-icono" aria-hidden="true">{car.icono}</span>
                      <div>
                        <strong className="caract-titulo">{car.titulo}</strong>
                        <p className="caract-desc">{car.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="modulo-detalle-cta">
                  <button className="btn-primary" onClick={scrollToForm}>
                    Quiero conocer más →
                  </button>
                </div>
              </div>
            );
          })()}
        </section>

        {/* ── AUTOMATIZACIÓN ─────────────────────────────────────────── */}
        <section className="automation" aria-labelledby="automation-heading">
          <div className="automation-header">
            <div className="automation-badge">⚡ Automatización total</div>
            <h2 id="automation-heading">
              ERP automatizado: POS, inventario y contabilidad en República Dominicana
            </h2>
            <p className="automation-sub">
              Desde que vendes hasta que reportas a DGII,{" "}
              <span className="highlight">todo queda automático</span>
            </p>
          </div>
          <div className="automation-grid">
            <div className="automation-card">
              <div className="automation-icon" aria-hidden="true">💳</div>
              <h3>Cada venta se registra</h3>
              <p>Sistema POS conectado en tiempo real. Cada transacción queda registrada automáticamente sin intervención manual.</p>
            </div>
            <div className="automation-card">
              <div className="automation-icon" aria-hidden="true">💱</div>
              <h3>Conversión automática a pesos</h3>
              <p>Multi-moneda con tasa actualizada. Todo se convierte a pesos dominicanos automáticamente.</p>
            </div>
            <div className="automation-card">
              <div className="automation-icon" aria-hidden="true">📊</div>
              <h3>Contabilidad sin intervención</h3>
              <p>Asientos contables generados automáticamente. Cierra tu mes contable en minutos, no en días.</p>
            </div>
            <div className="automation-card">
              <div className="automation-icon" aria-hidden="true">📄</div>
              <h3>Integración con e-CF DGII</h3>
              <p>Facturación electrónica conectada directamente con DGII. Cumple sin preocupaciones ni errores.</p>
            </div>
          </div>
          <div className="automation-diff">
            <p>
              💡 <strong>¿La diferencia?</strong> Mientras otros sistemas te piden hacer doble trabajo,
              eFIT hace todo por ti. QuickBooks, Odoo y SAP te dan herramientas.
              Nosotros te damos resultados automáticos.
            </p>
          </div>
        </section>

        {/* ── FEATURES ───────────────────────────────────────────────── */}
        <section className="features" aria-labelledby="features-heading">
          <div className="section-header">
            <h2 id="features-heading">Sistema POS con inventario en tiempo real para negocios en RD</h2>
            <p>Sin complicaciones, sin sistemas separados</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon" aria-label="Sistema POS en República Dominicana">💳</div>
              <h3>POS inteligente</h3>
              <p>Vende más rápido con un sistema POS intuitivo que se adapta a tu flujo de trabajo en RD</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-label="Control de inventario">📦</div>
              <h3>Inventario en tiempo real</h3>
              <p>Nunca te quedes sin stock. Alertas automáticas y control preciso de tu inventario</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-label="Software contable RD">📊</div>
              <h3>Contabilidad automatizada</h3>
              <p>Software contable para República Dominicana. Libros actualizados sin trabajo extra</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-label="Facturación electrónica DGII e-CF">📄</div>
              <h3>Facturación electrónica DGII</h3>
              <p>Genera e-CF automáticamente. Cumple con la normativa DGII sin errores ni multas</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-label="CRM integrado">👥</div>
              <h3>CRM integrado</h3>
              <p>Conoce a tus clientes y aumenta tus ventas recurrentes con seguimiento automatizado</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-label="Reportes ejecutivos DGII">📈</div>
              <h3>Reportes ejecutivos</h3>
              <p>Reportes 606, 607, 608 para DGII generados automáticamente. Toma decisiones con datos reales</p>
            </div>
          </div>
        </section>

        {/* ── e-CF ───────────────────────────────────────────────────── */}
        <section className="ecf-section" aria-labelledby="ecf-heading">
          <div className="ecf-content">
            <div className="ecf-badge">Facturación Electrónica DGII</div>
            <h2 id="ecf-heading">Facturación electrónica compatible con DGII (e-CF)</h2>
            <p className="ecf-description">
              La facturación electrónica (e-CF) en República Dominicana es obligatoria según la DGII
              bajo la Norma General 05-2019. Con eFIT, tu negocio cumple automáticamente con todos
              los requisitos fiscales: generación, envío y validación del comprobante electrónico
              sin errores, sin multas y sin sistemas adicionales.
            </p>
            <div className="ecf-benefits">
              <div className="ecf-benefit">
                <span className="ecf-check">✓</span>
                <span>Generación automática de e-CF</span>
              </div>
              <div className="ecf-benefit">
                <span className="ecf-check">✓</span>
                <span>Envío automático a DGII</span>
              </div>
              <div className="ecf-benefit">
                <span className="ecf-check">✓</span>
                <span>Reportes de cumplimiento fiscal</span>
              </div>
              <div className="ecf-benefit">
                <span className="ecf-check">✓</span>
                <span>Sin errores de validación DGII</span>
              </div>
            </div>
            <div className="ecf-note">
              💡 Disponible como servicio adicional en todos los planes (+${PRICING.ecf}/mes)
            </div>
          </div>
          <div className="ecf-visual" aria-hidden="true">
            <div className="ecf-card">
              <div className="ecf-card-icon">📄➡️🏛️</div>
              <p>e-CF automático</p>
              <small>100% compatible con DGII</small>
            </div>
          </div>
        </section>

        {/* ── PLANES POR INDUSTRIA ────────────────────────────────────── */}
        <section className="pricing-section" aria-labelledby="pricing-heading">
          <div className="section-header">
            <h2 id="pricing-heading">Planes de ERP para negocios en República Dominicana</h2>
            <p>Elige tu industria y ve los planes diseñados para ti</p>
          </div>

          {!selectedIndustry ? (
            /* ── SELECTOR DE INDUSTRIA ── */
            <div className="industry-selector">
              <p className="industry-selector-hint">¿Qué tipo de negocio tienes?</p>
              <div className="industry-grid">
                {INDUSTRY_LIST.map(ind => (
                  <button
                    key={ind.id}
                    className="industry-btn"
                    onClick={() => setSelectedIndustry(ind.id)}
                  >
                    <span className="industry-btn-label">{ind.label}</span>
                    <span className="industry-btn-sub">{ind.sub}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* ── PLANES DE LA INDUSTRIA SELECCIONADA ── */
            <div className="pricing-flow">
              <button className="back-btn" onClick={() => setSelectedIndustry(null)}>
                ← Cambiar industria
              </button>

              {selectedIndustry === "enterprise" ? (
                <div className="enterprise-box">
                  <div className="enterprise-box-icon">🏢</div>
                  <h3>Solución Enterprise</h3>
                  <p className="enterprise-tagline">Diseñado para empresas con múltiples verticales, sucursales o requisitos específicos.</p>
                  <ul className="enterprise-features">
                    <li>✓ Todos los módulos Comercial + Constructor</li>
                    <li>✓ Todos los módulos Contable + Renta-car</li>
                    <li>✓ e-CF Facturación Electrónica incluido</li>
                    <li>✓ Multi-empresa sin límite</li>
                    <li>✓ API personalizada</li>
                    <li>✓ Migración de datos incluida</li>
                    <li>✓ Implementación dedicada</li>
                    <li>✓ Soporte prioritario 24/7</li>
                  </ul>
                  <button className="enterprise-cta" onClick={() => seleccionarPlan("enterprise", "enterprise", "Enterprise", "$250")}>
                    Agendar llamada →
                  </button>
                </div>
              ) : (
                <>
                  <div className="plans-grid">
                    {PLANS_BY_INDUSTRY[selectedIndustry].map((plan, i) => (
                      <div
                        key={i}
                        className={`pricing-card${plan.highlight ? " pricing-card--highlight" : ""}`}
                      >
                        {plan.highlight && (
                          <div className="pricing-badge">⭐ Más popular</div>
                        )}
                        <div className="pricing-card-header">
                          <h3 className="pricing-card-name">{plan.name}</h3>
                          <div className="pricing-card-price">
                            <span className="pricing-amount">{plan.price}</span>
                            <span className="pricing-period">/mes + ITBIS</span>
                          </div>
                          <p className="pricing-tagline">{plan.tagline}</p>
                        </div>

                        <div className="pricing-limits">
                          {plan.limits.map((l, idx) => (
                            <span key={idx} className="pricing-limit">{l}</span>
                          ))}
                        </div>

                        <ul className="pricing-features">
                          {plan.features.map((f, idx) => (
                            <li key={idx}>
                              <span className="pricing-check" aria-hidden="true">✓</span>
                              {f}
                            </li>
                          ))}
                        </ul>

                        <button
                          className={`pricing-cta${plan.highlight ? " pricing-cta--primary" : ""}`}
                          onClick={() => seleccionarPlan(plan.planKey, selectedIndustry, plan.name, plan.price)}
                        >
                          🚀 Probar gratis {DEMO_DAYS} días
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* ── ADD-ONS ── */}
                  <div className="addons-section">
                    <h4>Agrega a tu plan</h4>
                    <div className="addons-grid">
                      <div className="addon-item">
                        <span className="addon-icon">📄</span>
                        <div className="addon-info">
                          <strong>Facturación electrónica (e-CF)</strong>
                          <span className="addon-price">+$30/mes</span>
                          <span className="addon-tag">Requerido DGII</span>
                        </div>
                      </div>
                      <div className="addon-item">
                        <span className="addon-icon">👥</span>
                        <div className="addon-info">
                          <strong>CRM</strong>
                          <span className="addon-price">+$15/mes</span>
                        </div>
                      </div>
                      <div className="addon-item">
                        <span className="addon-icon">🏢</span>
                        <div className="addon-info">
                          <strong>Multi-empresa</strong>
                          <span className="addon-price">+$25/mes</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {/* ── CONFIANZA ──────────────────────────────────────────────── */}
        <section className="trust" aria-label="Empresas que usan eFIT">
          <div className="trust-content">
            <h3>+500 negocios en República Dominicana ya usan eFIT</h3>
            <div className="trust-logos" aria-label="Tipos de negocio">
              <span>Comercios</span>
              <span>Servicios</span>
              <span>Contadores</span>
              <span>Distribuidores</span>
            </div>
          </div>
        </section>

        {/* ── SECCIÓN SEO: ¿QUÉ ES e-CF? ────────────────────────────── */}
        <section className="ecf-info-section" aria-labelledby="ecf-info-heading">
          <div className="ecf-info-content">
            <h2 id="ecf-info-heading">¿Qué es la facturación electrónica en República Dominicana?</h2>
            <p>
              La facturación electrónica (e-CF) es el sistema oficial de comprobantes fiscales
              electrónicos establecido por la <strong>Dirección General de Impuestos Internos (DGII)</strong>
              de República Dominicana mediante la Norma General 05-2019. Sustituye los comprobantes
              fiscales en papel y exige que las empresas generen, firmen y transmitan sus facturas
              en formato digital directamente al sistema de la DGII en tiempo real.
            </p>
            <p>
              La implementación es <strong>obligatoria para todos los contribuyentes</strong> del
              Registro Nacional de Contribuyentes (RNC) y su incumplimiento genera multas y
              sanciones fiscales. eFIT integra el proceso completo de e-CF de forma automática:
              desde la generación del comprobante al momento de la venta, hasta el envío,
              validación y almacenamiento del acuse de recibo de la DGII — sin que tu equipo
              tenga que hacer nada manualmente.
            </p>
            <div className="ecf-info-cta">
              <button className="btn-primary" onClick={scrollToForm}>
                Conocer cómo eFIT maneja tu e-CF
              </button>
            </div>
          </div>
        </section>

        {/* ── FORMULARIO ─────────────────────────────────────────────── */}
        <section id="form-section" className="form-section" aria-labelledby="form-heading">
          <div className="section-header">
            <h2 id="form-heading">Prueba gratis tu sistema ERP en República Dominicana</h2>
            <p>Sin compromiso. Sin tarjeta de crédito. Un asesor te contactará en 24 horas.</p>
          </div>

          {showSuccess && (
            <div id="success-message" className="success-message" role="alert">
              <div className="success-icon">✅</div>
              <h3>¡Revisa tu correo!</h3>
              <p>
                Enviamos un email de confirmación a <strong>{submittedEmail}</strong>.
                <br />
                <span className="success-note">Haz clic en el enlace para completar tu solicitud.</span>
              </p>
              <p style={{ fontSize: "13px", color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: "8px", padding: "10px 14px", margin: "12px 0 0" }}>
                💡 <strong>¿No lo ves?</strong> Revisa tu carpeta de <strong>Spam</strong> o <strong>Correo no deseado</strong>.
              </p>
              <div className="success-actions">
                <button className="success-btn" onClick={resetForm}>
                  Enviar otra solicitud
                </button>
              </div>
            </div>
          )}

          {submitError && (
            <div className="error-message" role="alert">
              <span aria-hidden="true">⚠️</span>
              <p>{submitError}</p>
              <button onClick={() => setSubmitError(null)} aria-label="Cerrar error">✕</button>
            </div>
          )}

          {!showSuccess && (
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot anti-bot — invisible para humanos, visible para bots */}
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ opacity: 0, position: "absolute", height: 0, width: 0, pointerEvents: "none" }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nombre" className="visually-hidden">Nombre completo</label>
                  <input
                    id="nombre"
                    name="nombre"
                    type="text"
                    placeholder="Nombre completo"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    aria-label="Nombre completo"
                    aria-required="true"
                    autoComplete="name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="empresa" className="visually-hidden">Nombre de la empresa</label>
                  <input
                    id="empresa"
                    name="empresa"
                    type="text"
                    placeholder="Nombre de tu empresa"
                    value={formData.empresa}
                    onChange={handleChange}
                    required
                    aria-label="Nombre de la empresa"
                    aria-required="true"
                    autoComplete="organization"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="visually-hidden">Email corporativo</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Email corporativo"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    aria-label="Email corporativo"
                    aria-required="true"
                    autoComplete="email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="telefono" className="visually-hidden">Teléfono de contacto</label>
                  <input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    placeholder="Ej: +1 809 123 4567"
                    pattern="^\+?[0-9\s]{8,15}$"
                    title="Incluye código país. Ej: +18091234567"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    aria-label="Teléfono de contacto"
                    aria-required="true"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="rnc_cedula" className="visually-hidden">RNC o Cédula</label>
                  <input
                    id="rnc_cedula"
                    name="rnc_cedula"
                    type="text"
                    placeholder="RNC o Cédula (opcional)"
                    value={formData.rnc_cedula}
                    onChange={handleChange}
                    aria-label="RNC o Cédula"
                    maxLength={11}
                  />
                </div>
              </div>

              <div className="form-row">
                {planCtaSeleccionado ? (
                  /* Plan pre-seleccionado desde la sección de pricing */
                  <div className="form-group form-group--full">
                    <div className="plan-pill">
                      <span className="plan-pill-icon">📋</span>
                      <span className="plan-pill-nombre">{planCtaSeleccionado.nombre}</span>
                      <span className="plan-pill-precio">{planCtaSeleccionado.precio}/mes</span>
                      <button
                        type="button"
                        className="plan-pill-cambiar"
                        onClick={() => setPlanCtaSeleccionado(null)}
                      >
                        Cambiar plan
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Sin plan elegido — selectores libres */
                  <>
                    <div className="form-group">
                      <label htmlFor="plan" className="visually-hidden">Plan</label>
                      <select
                        id="plan"
                        name="plan"
                        value={formData.plan}
                        onChange={handleChange}
                        aria-label="Selecciona un plan"
                      >
                        <option value="basico">Plan Básico - ${PRICING.plans.basico}/mes</option>
                        <option value="plus">Plan Plus - ${PRICING.plans.plus}/mes</option>
                        <option value="enterprise">Plan Enterprise - Personalizado</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="tipo_negocio" className="visually-hidden">Tipo de negocio</label>
                      <select
                        id="tipo_negocio"
                        name="tipo_negocio"
                        value={formData.tipo_negocio}
                        onChange={handleChange}
                        aria-label="Tipo de negocio"
                      >
                        <option value="comercio">🏪 Comercio / Retail</option>
                        <option value="constructor">🏗️ Constructor / Ingeniería</option>
                        <option value="servicios">💼 Servicios profesionales</option>
                        <option value="contable">📊 Oficina contable / Consultor</option>
                        <option value="rentacar">🚗 Renta-car / Alquiler</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <label className="checkbox" htmlFor="requiere_ecf">
                <input
                  id="requiere_ecf"
                  type="checkbox"
                  name="requiere_ecf"
                  checked={formData.requiere_ecf}
                  onChange={handleChange}
                  aria-label="Necesito facturación electrónica e-CF"
                />
                📄 Necesito facturación electrónica e-CF (servicio adicional +${PRICING.ecf}/mes)
              </label>

              <button
                type="submit"
                disabled={loading}
                className="submit-btn"
                aria-busy={loading}
              >
                {getButtonText()}
              </button>

              <p className="form-note">
                Al enviar tu solicitud, aceptas nuestros términos de servicio y política de privacidad.
                <br />
                🔒 Tus datos están protegidos y no compartimos tu información.
              </p>
            </form>
          )}
        </section>

      </div>

      <ChatWidget />
    </>
  );
}
